import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Menu,
  MenuItem,
  Collapse,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useProducts } from '../context/ProductsContext';

const navItems = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Blog', to: '/blog' },
  { label: 'Contact', to: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productsAnchor, setProductsAnchor] = useState(null);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const location = useLocation();
  const { navProducts, loading: productsLoading } = useProducts();
  const products = navProducts;

  const isProductsActive = location.pathname === '/products' || location.pathname.startsWith('/products/');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 15);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMobileProductsOpen(false);
    setProductsAnchor(null);
  }, [location.pathname]);

  const linkSx = (active) => ({
    color: scrolled ? '#1a1a1a' : 'rgba(255,255,255,0.9)',
    fontWeight: 900,
    fontSize: '0.75rem',
    letterSpacing: '1.2px',
    px: 2,
    textTransform: 'uppercase',
    minWidth: 0,
    '&:hover': { color: scrolled ? '#fff' : '#ff6d00', bgcolor: 'transparent', textDecoration: 'none' },
    ...(active && { color: scrolled ? '#fff' : '#ff6d00' }),
  });

  return (
    <>
      <AppBar
        component={motion.nav}
        elevation={0}
        position="fixed"
        sx={{
          top: 0,
          left: 0,
          right: 0,
          background: 'transparent',
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          transition: 'all 0.4s ease',
          pt: scrolled ? 1.5 : 0,
        }}
      >
        <Box
          sx={{
            width: scrolled ? '94%' : '100%',
            maxWidth: scrolled ? '1200px' : '100%',
            bgcolor: scrolled ? '#ff6d00' : '#1a1a1a',
            borderRadius: scrolled ? '8px' : '0px',
            borderTop: 'none',
            borderBottom: scrolled ? 'none' : '1px solid rgba(255,255,255,0.1)',
            boxShadow: scrolled ? '0 25px 50px -12px rgba(0, 0, 0, 0.25)' : 'none',
            transition: 'all 0.5s cubic-bezier(0.2, 0.8, 0.2, 1)',
            px: { xs: 2, md: 5 },
          }}
        >
          <Toolbar disableGutters sx={{ height: { xs: scrolled ? 56 : 72, md: scrolled ? 65 : 85 }, minHeight: { xs: 56, md: 65 } }}>
            <Box component={RouterLink} to="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', gap: 1.5 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: '#ff6d00',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transform: 'skewX(-15deg)',
                  boxShadow: scrolled ? 'none' : '4px 4px 0px #fff',
                }}
              >
                <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '0.9rem', transform: 'skewX(15deg)' }}>M</Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 900, color: scrolled ? '#1a1a1a' : '#ff6d00', letterSpacing: '-0.5px' }}>
                MODURAX
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} sx={{ flexGrow: 1, justifyContent: 'center', display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
              {navItems.slice(0, 2).map((item) => (
                <Button key={item.label} component={RouterLink} to={item.to} sx={linkSx(location.pathname === item.to)}>
                  {item.label}
                </Button>
              ))}

              <Button
                onClick={(e) => setProductsAnchor(e.currentTarget)}
                endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '1rem !important' }} />}
                sx={linkSx(isProductsActive)}
              >
                Products
              </Button>
              <Menu
                anchorEl={productsAnchor}
                open={Boolean(productsAnchor)}
                onClose={() => setProductsAnchor(null)}
                PaperProps={{
                  sx: {
                    mt: 1,
                    borderRadius: 0,
                    minWidth: 300,
                    maxWidth: 360,
                    border: '1px solid #e8e8e8',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                  },
                }}
              >
                <MenuItem
                  component={RouterLink}
                  to="/products"
                  onClick={() => setProductsAnchor(null)}
                  sx={{ fontWeight: 800, color: '#ff6d00', fontSize: '0.85rem', py: 1.25 }}
                >
                  All Products
                </MenuItem>
                {productsLoading ? (
                  <MenuItem disabled sx={{ fontSize: '0.85rem', py: 1.25 }}>
                    Loading products...
                  </MenuItem>
                ) : (
                  products.map((product) => (
                    <MenuItem
                      key={product.id}
                      component={RouterLink}
                      to={`/products/${product.id}`}
                      onClick={() => setProductsAnchor(null)}
                      selected={location.pathname === `/products/${product.id}`}
                      sx={{
                        whiteSpace: 'normal',
                        py: 1.25,
                        fontSize: '0.82rem',
                        fontWeight: location.pathname === `/products/${product.id}` ? 800 : 500,
                        lineHeight: 1.4,
                      }}
                    >
                      {product.name}
                    </MenuItem>
                  ))
                )}
              </Menu>

              {navItems.slice(2).map((item) => (
                <Button key={item.label} component={RouterLink} to={item.to} sx={linkSx(location.pathname === item.to)}>
                  {item.label}
                </Button>
              ))}
            </Stack>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                component={RouterLink}
                to="/#requirement-form"
                variant="contained"
                disableElevation
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  bgcolor: scrolled ? '#1a1a1a' : '#ff6d00',
                  color: '#fff',
                  borderRadius: 0,
                  px: 3,
                  py: 1,
                  fontWeight: 900,
                  fontSize: '0.75rem',
                  '&:hover': { bgcolor: '#fff', color: '#1a1a1a' },
                }}
              >
                REQUEST INFO
              </Button>

              <IconButton onClick={() => setMobileOpen(true)} sx={{ display: { md: 'none' }, color: scrolled ? '#1a1a1a' : '#fff' }}>
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Box>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 360 }, maxWidth: '100vw', bgcolor: '#ff6d00', p: { xs: 3, sm: 4 } } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={() => setMobileOpen(false)} sx={{ color: '#fff' }}>
            <CloseIcon fontSize="large" />
          </IconButton>
        </Box>
        <List sx={{ mt: 4 }}>
          {navItems.map((item) => (
            <ListItem key={item.to} disablePadding sx={{ mb: 1.5 }}>
              <ListItemButton component={RouterLink} to={item.to} onClick={() => setMobileOpen(false)}>
                <Typography sx={{ fontSize: { xs: '2rem', sm: '2.5rem' }, fontWeight: 900, color: '#fff', lineHeight: 1.1, textTransform: 'uppercase' }}>
                  {item.label}
                </Typography>
              </ListItemButton>
            </ListItem>
          ))}

          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton onClick={() => setMobileProductsOpen((open) => !open)}>
              <Typography sx={{ fontSize: { xs: '2rem', sm: '2.5rem' }, fontWeight: 900, color: '#fff', lineHeight: 1.1, textTransform: 'uppercase' }}>
                Products
              </Typography>
              <KeyboardArrowDownIcon sx={{ color: '#fff', ml: 1, transform: mobileProductsOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
            </ListItemButton>
          </ListItem>
          <Collapse in={mobileProductsOpen}>
            <List disablePadding sx={{ pl: 2, mb: 2 }}>
              <ListItem disablePadding>
                <ListItemButton component={RouterLink} to="/products" onClick={() => setMobileOpen(false)}>
                  <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: '1rem' }}>All Products</Typography>
                </ListItemButton>
              </ListItem>
              {productsLoading ? (
                <ListItem disablePadding>
                  <ListItemButton disabled>
                    <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.92rem' }}>Loading products...</Typography>
                  </ListItemButton>
                </ListItem>
              ) : (
                products.map((product) => (
                  <ListItem key={product.id} disablePadding>
                    <ListItemButton component={RouterLink} to={`/products/${product.id}`} onClick={() => setMobileOpen(false)}>
                      <Typography sx={{ color: 'rgba(255,255,255,0.95)', fontSize: '0.92rem', lineHeight: 1.4, py: 0.5 }}>
                        {product.name}
                      </Typography>
                    </ListItemButton>
                  </ListItem>
                ))
              )}
            </List>
          </Collapse>
        </List>
      </Drawer>
    </>
  );
}
